import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { queryKeys } from '@/shared/api/queryKeys';
import {
  apiNotificationsGet,
  apiNotificationsUnreadCountGet,
  resolveNotificationLang,
} from '@/entities/notifications/modules/api/notifications';

export const useNotifications = (page?: number) => {
  const { i18n } = useTranslation();
  const lang = resolveNotificationLang(i18n.language);
  return useQuery({
    queryKey: queryKeys.notifications.list(page, lang),
    queryFn: () => apiNotificationsGet(page, lang),
    staleTime: 0,
  });
};

const NOTIFICATIONS_PAGE_SIZE = 10;

/**
 * Page-based infinite list with 10-per-fetch chunks. The backend uses DRF
 * page pagination; `next` is a non-null URL while more rows exist, so we
 * track the next page number ourselves to keep the param shape stable
 * across reconnects + cache invalidations.
 */
export const useNotificationsInfinite = () => {
  const { i18n } = useTranslation();
  const lang = resolveNotificationLang(i18n.language);
  return useInfiniteQuery({
    queryKey: [...queryKeys.notifications.all, 'infinite', lang] as const,
    queryFn: ({ pageParam }) =>
      apiNotificationsGet(pageParam, lang, NOTIFICATIONS_PAGE_SIZE),
    initialPageParam: 1 as number,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.next ? allPages.length + 1 : undefined,
    staleTime: 0,
  });
};

// Unread count is invalidated by the realtime WS provider on inbound
// `{type:'invalidate', query_key:'notifications'}` frames, so no polling.
export const useNotificationsUnreadCount = () =>
  useQuery({
    queryKey: queryKeys.notifications.unreadCount,
    queryFn: apiNotificationsUnreadCountGet,
    staleTime: 0,
  });
